import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref, watch, watchEffect } from "vue";
  import { formatPrice } from "@/helpers/transformation/price";
  import { copyToClipboard } from "@/helpers/copyToClipboard";
  import { useToast } from "vue-toastification";
  import { useRouter } from "vue-router";
  import store from "@/store";
  import {
    BuilderPackage,
    FacadePackage,
    FloorplanPackage,
    HousePackage,
    LotPackage,
    UpdateBuilderQuoteDto,
  } from "@/types/BuilderQuote";
  import LeadSourceEnum from "@/enums/LeadSourceEnum";
  import { TimeToBuildEnum } from "@/enums/BuilderTimeToBuild";
  import { BestDescribesYouEnum } from "@/enums/BuilderBestDescribesYou";
  import { FinanceStatusEnum } from "@/enums/BuilderFinanceStatus";
  import router from "@/router";
  import { LotInterface } from "@/types/LotInterface";
  import { calculateBuildPriceOrPackagePriceTotal } from "@/helpers/builder/calculateBuildPriceOrPackagePriceTotal";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'SavedChanges',
  props: {
    landSize: {
      type: Number,
      default: 0,
    },
    siteCosts: {
      type: Number,
      default: () => 0,
    },
    lotPackage: {
      type: Object,
      default: Object as () => LotPackage,
    },
    builderPackage: {
      type: Object,
      default: Object as () => BuilderPackage,
    },
    housePackage: {
      type: Object,
      default: Object as () => HousePackage,
    },
    facadePackage: {
      type: Object,
      default: Object as () => FacadePackage,
    },
    floorplanPackage: {
      type: Object,
      default: Object as () => FloorplanPackage,
    },
    landowerId: {
      type: String,
      default: () => "",
    },
    estateId: {
      type: String,
      default: () => "",
    },
    estateTitle: {
      type: String,
      default: () => "",
    },
    isUpdate: {
      type: Boolean,
      default: false,
    },
  },
  setup(__props, { expose: __expose }) {
  __expose();

  const props = __props;
  const packageId = router.currentRoute.value.params.packageId;
  const toast = useToast();
  const isLoading = ref(false);
  const routerVue = useRouter();
  const lot = ref<LotInterface | null>(null);
  const newSiteCosts = ref(props.siteCosts);

  watch(
    () => props.siteCosts,
    (newVal) => {
      newSiteCosts.value = newVal;
    }
  );

  const getLot = async () => {
    try {
      const res = await store.dispatch("lot/getLot", props.lotPackage.lotId);

      if (res.data) {
        lot.value = res.data;
      }
    } catch (error) {
      toast.error("An error occurred while getting the lot");
    }
  };
  const updateSiteCosts = async () => {
    try {
      const updatedBuilders = lot.value?.builders.map((builder) => {
        if (builder.builderId.toString() === props.builderPackage.builderId) {
          const updatedHouses = builder.housesIds.map((house) => {
            if (house.houseId.toString() === props.housePackage.houseId) {
              house.siteCosts = newSiteCosts.value;
            }

            return house;
          });

          builder.housesIds = updatedHouses;
        }

        return builder;
      });

      const updatedLot = {
        ...lot,
        builders: updatedBuilders,
      };

      await store.dispatch("lot/updateLot", {
        id: props.lotPackage.lotId,
        data: updatedLot,
      });
    } catch (error) {
      toast.error("Error updating site costs");
    } finally {
      isLoading.value = false;
    }
  };
  const onSavePackage = async () => {
    try {
      isLoading.value = true;
      const body = createBodyRequest();

      await store.dispatch("builder/sendQuote", body);
      await updateSiteCosts();
      toast.success("The package was successfully created");
      routerVue.push({ name: "AdminPackages" });
    } catch (error) {
      toast.error("Error create package");
    } finally {
      isLoading.value = false;
    }
  };
  const onUpdatePackage = async () => {
    try {
      isLoading.value = true;
      const body = createBodyRequest();

      await store.dispatch("builder/updatePackage", {
        quoteId: packageId,
        data: body,
      });
      await updateSiteCosts();
      toast.success("The package was successfully updated");
      routerVue.push({ name: "AdminPackages" });
    } catch (error) {
      toast.error("Error update package");
    } finally {
      isLoading.value = false;
    }
  };
  const createBodyRequest = () => {
    const userSelection = {
      builder: {
        builderId: props.builderPackage.builderId,
        name: props.builderPackage.name as string,
        logoUrl: props.builderPackage.logoUrl as string,
      },
      house: {
        houseId: props.housePackage.houseId,
        name: props.housePackage.name as string,
        price: props.housePackage.price as number,
      },
      facade: {
        facadeId: props.facadePackage.facadeId,
        name: props.facadePackage.name,
        url: props.facadePackage.url,
        price: props.facadePackage.price,
      },
      floorplan: {
        name: props.floorplanPackage.name,
        url: props.floorplanPackage.url,
      },
      lot: {
        lotId: props.lotPackage.lotId,
        name: props.lotPackage.name as string,
        price: props.lotPackage.price as number,
      },
    };

    return {
      selections: props.isUpdate ? userSelection : [userSelection],
      additionalRequest: "",
      timeReference: TimeToBuildEnum.THREE_MONTHS,
      bestDescribesYou: BestDescribesYouEnum.FIRST_TIME_BUYER,
      financeStatus: FinanceStatusEnum.PRE_APPROVED,
      estateTitle: props.estateTitle,
      estateId: props.estateId,
      landownerId: props.landowerId,
      emailSentToLandowner: false,
      isAdminPackage: true,
      lead: {
        landownerId: props.landowerId,
        estateId: props.estateId,
        firstName: "Lottrackr",
        lastName: "Concierge",
        email: "concierge@lottrackr.com",
        phone: "0000000000",
        source: LeadSourceEnum.ENQUIRY,
        watched: false,
      },
    };
  };

  const onCopyLinkPackage = async () => {
    const link = `${window.location.origin}/builder/estate/${props.estateId}/borrow-calculator/request-quote/${packageId}/house/${props.housePackage.houseId}/loan-amount/600000`;

    copyToClipboard(link, "Saved Package link was copied to clipboard");
  };

  onMounted(() => {
    if (props.lotPackage.lotId) {
      getLot();
    }
  });

const __returned__ = { props, packageId, toast, isLoading, routerVue, lot, newSiteCosts, getLot, updateSiteCosts, onSavePackage, onUpdatePackage, createBodyRequest, onCopyLinkPackage, computed, onMounted, reactive, ref, watch, watchEffect, get formatPrice() { return formatPrice }, get copyToClipboard() { return copyToClipboard }, get useToast() { return useToast }, get useRouter() { return useRouter }, get store() { return store }, get BuilderPackage() { return BuilderPackage }, get FacadePackage() { return FacadePackage }, get FloorplanPackage() { return FloorplanPackage }, get HousePackage() { return HousePackage }, get LotPackage() { return LotPackage }, get UpdateBuilderQuoteDto() { return UpdateBuilderQuoteDto }, get LeadSourceEnum() { return LeadSourceEnum }, get TimeToBuildEnum() { return TimeToBuildEnum }, get BestDescribesYouEnum() { return BestDescribesYouEnum }, get FinanceStatusEnum() { return FinanceStatusEnum }, get router() { return router }, get LotInterface() { return LotInterface }, get calculateBuildPriceOrPackagePriceTotal() { return calculateBuildPriceOrPackagePriceTotal } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})